import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import SetOrderDialog from './components/SetOrderDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import AboutUsListSummaryFilterSection from './components/AboutUsListSummaryFilterSection';
import { useGetAboutUsListQuery } from '@/api/information/aboutUsApiSlice';
import {
	AboutUsPageFilter,
	InformationAboutUsDTO,
} from '@/api/DTO/information/aboutUs.interface';
import { useGetInformationTypeQuery } from '@/api/common';
import { InformationType } from '@/api/enum/common.enum';
import SetSubTitleDialog from './components/SetSubTitleDialog';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function AboutUsListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();

	const { data: informationTypeData } = useGetInformationTypeQuery(
		InformationType.aboutUs,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const defaultPropertyFilter: AboutUsPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<AboutUsPageFilter> = defaultPropertyFilter;
	const methods = useForm<AboutUsPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: AboutUsPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetAboutUsListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: InformationAboutUsDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as AboutUsPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('aboutUs.cn_title'),
			flex: 25,
		},
		{
			field: 'titleEn',
			headerName: t('aboutUs.en_title'),
			flex: 25,
		},
		{
			field: 'updatedAt',
			headerName: t('aboutUs.lastUpdateTime'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('aboutUs.createTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/information-manage/about-us-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.aboutUsList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/information-manage/about-us-list/add`)}
					>
						{t('drawerListItems.addPage')}
					</Button>
				</Stack>
			</Box>
			<Box display='flex' alignItems={'center'} marginTop={'12px'}>
				<Typography variant='body1'>
					{t('aboutUs.subTitle')}：
					{informationTypeData?.data.subtitleZh
						? informationTypeData?.data.subtitleZh
						: informationTypeData?.data.subtitleEn
						? informationTypeData?.data.subtitleEn
						: '-'}
				</Typography>
				<SetSubTitleDialog
					informationTypeData={informationTypeData}
				></SetSubTitleDialog>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<AboutUsListSummaryFilterSection></AboutUsListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
